// Sessions list view. fn-1.29; redesigned to the Smokeball design system's
// day-grouped activity list (design 1c).
//
// **Acceptance**: "Runs list shows top-50 (paginated); each row links to
// `/runs/:id`."
//
// **Wiring**: `trpc.runs.list.useQuery({ limit: 50 })`. The control plane
// caps at 200; we default to 50. Pagination is prev / load-more over the
// upstream cursor.
//
// **Design (1c)**: rows are grouped by day of creation (Pinned → Today →
// Yesterday → older), each group a `.card` with row dividers. A segmented
// All / Needs you / Running / Done filter buckets the *loaded* page
// client-side (the buckets are multi-status; the CP `status` param only
// accepts a single status, so no server-side param can express them). Star
// a session to pin it above the day groups.

import { keepPreviousData } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  langfuseConfigured,
  maSessionUrl,
  openLangfuseTrace,
} from '../../config/observability-links.js';
import { SbIcon } from '../../design/sb-icon.js';
import { useFavorites } from '../../prefs/index.js';
import { trpc } from '../../trpc-client.js';
import { sessionStatusLabel } from '../format.js';
import './runs-list.css';

const PAGE_SIZE = 50;

// Agents are global and few (fn-8 / AIP-28). We resolve the session's
// `agent_id` → name client-side via a single `agents.list` query rather than
// coupling the BFF `runs.list` proxy to a schema version (the proxy forwards
// verbatim by design). 200 is the CP page cap — one query covers the full set.
// fn-52.1 / R1.
const AGENTS_LIST_LIMIT = 200;

/**
 * Build the `agent_id → name` lookup the Agent name renders from. Pure so it
 * unit-tests without mounting the page; the caller wraps it in `useMemo`.
 */
export function buildAgentNameMap(
  agents: ReadonlyArray<{ readonly id: string; readonly name: string }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const agent of agents) {
    if (agent.id.length > 0) map.set(agent.id, agent.name);
  }
  return map;
}

/**
 * Resolve the label shown for a session's agent. Falls back to a truncated
 * `agent_id` when the name is unresolved (agent not listable for the caller,
 * or list still loading) — never blank. fn-52.1 / R1.
 */
export function resolveAgentLabel(agentId: string, names: ReadonlyMap<string, string>): string {
  const name = names.get(agentId);
  if (name !== undefined && name.length > 0) return name;
  return agentId.length > 8 ? `${agentId.slice(0, 8)}…` : agentId;
}

// --- Status buckets (the segmented filter) --------------------------------

export type SessionBucket = 'all' | 'needs-you' | 'running' | 'done';

export const SESSION_BUCKETS: ReadonlyArray<{
  readonly value: SessionBucket;
  readonly label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'needs-you', label: 'Needs you' },
  { value: 'running', label: 'Running' },
  { value: 'done', label: 'Done' },
];

/**
 * Map a raw session status onto one of the three non-`all` buckets.
 *
 * `awaiting_*` → Needs you; the terminal states → Done; everything else
 * (running / pending / initializing / idle, and any unknown status) → Running,
 * so a live session is never hidden under the terminal "Done" filter.
 */
export function sessionStatusBucket(status: string): Exclude<SessionBucket, 'all'> {
  switch (status) {
    case 'awaiting_approval':
    case 'awaiting_user':
      return 'needs-you';
    case 'completed':
    case 'failed':
    case 'cancelled':
    case 'expired':
    case 'terminated':
    case 'rejected':
      return 'done';
    default:
      return 'running';
  }
}

export function matchesBucket(status: string, bucket: SessionBucket): boolean {
  return bucket === 'all' || sessionStatusBucket(status) === bucket;
}

function bucketLabel(bucket: SessionBucket): string {
  return SESSION_BUCKETS.find((b) => b.value === bucket)?.label ?? 'All';
}

// --- Per-row derived data --------------------------------------------------

/** Opaque favourites key for a session (mirrors the agents list's convention). */
export function sessionFavoriteKey(sessionId: string): string {
  return `session:${sessionId}`;
}

/**
 * Human day label for a session's `created_at`: Today / Yesterday / a
 * formatted date (with the year only when it differs from now). Compares
 * *local* calendar days. `now` is injected so it is deterministic to test.
 */
export function sessionDayLabel(iso: string, now: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown date';
  const startOf = (x: Date): number =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** Short time-of-day (e.g. "2:49 pm") for the right-aligned column. */
export function sessionTimeOfDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * The one-line summary under the agent name. Uses ONLY the conventional
 * `title` metadata key (the conversation subject, see `packages/ui/README`) —
 * we never synthesise a summary. Absent / blank → no line rendered.
 */
export function sessionSummary(
  metadata: Record<string, unknown> | null | undefined,
): string | undefined {
  if (metadata === null || metadata === undefined) return undefined;
  const title = (metadata as { title?: unknown }).title;
  if (typeof title === 'string' && title.trim().length > 0) return title.trim();
  return undefined;
}

/** The soft-delete flag (`metadata.archived === true`). */
export function isSessionArchived(metadata: Record<string, unknown> | null | undefined): boolean {
  return (metadata as { archived?: unknown } | null | undefined)?.archived === true;
}

export interface SessionVisual {
  /** Leading status icon; the `'dot'` sentinel renders a pulsing brand dot. */
  readonly icon:
    | 'dot'
    | 'circle-check-solid'
    | 'circle-exclamation-solid'
    | 'diamond-exclamation-solid';
  /** CSS expression for the icon tint. */
  readonly iconColor: string;
  /** `.tag` variant class (already includes the base `tag`). */
  readonly tagClassName: string;
  /** True when the tag needs the design's inline danger colours (Failed). */
  readonly tagDanger: boolean;
}

/** Map a status to its leading icon + status-tag styling (design 1c). */
export function sessionVisual(status: string): SessionVisual {
  const bucket = sessionStatusBucket(status);
  if (bucket === 'needs-you') {
    return {
      icon: 'diamond-exclamation-solid',
      iconColor: 'var(--color-text-warning)',
      tagClassName: 'tag tag--orange',
      tagDanger: false,
    };
  }
  if (bucket === 'running') {
    return {
      icon: 'dot',
      iconColor: 'var(--color-background-brand)',
      tagClassName: 'tag tag--cyan',
      tagDanger: false,
    };
  }
  // done
  if (status === 'failed' || status === 'rejected') {
    return {
      icon: 'circle-exclamation-solid',
      iconColor: 'var(--color-text-danger)',
      tagClassName: 'tag',
      tagDanger: true,
    };
  }
  if (status === 'completed') {
    return {
      icon: 'circle-check-solid',
      iconColor: 'var(--color-text-success)',
      tagClassName: 'tag tag--green',
      tagDanger: false,
    };
  }
  // cancelled / expired / terminated — terminal but not an error.
  return {
    icon: 'circle-exclamation-solid',
    iconColor: 'var(--fg-subtle)',
    tagClassName: 'tag tag--grey',
    tagDanger: false,
  };
}

// --- Grouping --------------------------------------------------------------

/** Minimal row shape the grouping needs; the query rows satisfy it. */
export interface SessionRowLike {
  readonly id: string;
  readonly status: string;
  readonly created_at: string;
}

export interface SessionGroup<T extends SessionRowLike> {
  /** Stable React key. */
  readonly key: string;
  /** Group heading ("Pinned" / "Today" / "Yesterday" / a date). */
  readonly label: string;
  readonly pinned: boolean;
  readonly runs: readonly T[];
}

/**
 * Build the ordered render groups: the active bucket filter applied first,
 * then the pinned (favourited) sessions hoisted into a "Pinned" group ABOVE
 * the day groups (Today → Yesterday → older, in the CP's newest-first order).
 * A pinned session appears only in the Pinned group, never duplicated below.
 */
export function buildSessionGroups<T extends SessionRowLike>(opts: {
  readonly runs: readonly T[];
  readonly bucket: SessionBucket;
  readonly favorites: ReadonlySet<string>;
  readonly now: Date;
}): ReadonlyArray<SessionGroup<T>> {
  const { runs, bucket, favorites, now } = opts;
  const pinned: T[] = [];
  const rest: T[] = [];
  for (const run of runs) {
    if (!matchesBucket(run.status, bucket)) continue;
    if (favorites.has(sessionFavoriteKey(run.id))) pinned.push(run);
    else rest.push(run);
  }
  const groups: Array<SessionGroup<T>> = [];
  if (pinned.length > 0) {
    groups.push({ key: 'pinned', label: 'Pinned', pinned: true, runs: pinned });
  }
  // Day groups in first-seen order — the CP returns rows newest-first, so the
  // natural iteration order is already Today → Yesterday → older.
  const byDay = new Map<string, T[]>();
  for (const run of rest) {
    const label = sessionDayLabel(run.created_at, now);
    const existing = byDay.get(label);
    if (existing === undefined) byDay.set(label, [run]);
    else existing.push(run);
  }
  for (const [label, dayRuns] of byDay) {
    groups.push({ key: `day:${label}`, label, pinned: false, runs: dayRuns });
  }
  return groups;
}

// --- Presentational sub-components (prop-driven, provider-free) ------------

/**
 * Per-row pin toggle. Prop-driven (no prefs hook) so it stays a pure,
 * testable component; the page wires the live `useFavorites()` state in.
 * `aria-pressed` carries the toggle state for AT. `stopPropagation` keeps a
 * star click from triggering the row's stretched navigation link.
 */
export function SessionStar(props: {
  readonly favorited: boolean;
  readonly sessionLabel: string;
  readonly onToggle: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      className={props.favorited ? 'runs-star is-favorited' : 'runs-star'}
      aria-pressed={props.favorited}
      aria-label={props.favorited ? `Unpin ${props.sessionLabel}` : `Pin ${props.sessionLabel}`}
      title={props.favorited ? 'Unpin session' : 'Pin session'}
      onClick={(e) => {
        e.stopPropagation();
        props.onToggle();
      }}
    >
      <SbIcon name="star-solid" size={16} />
    </button>
  );
}

/** Fields a rendered session row needs (the query rows satisfy this). */
interface SessionRowData extends SessionRowLike {
  readonly agent_id: string;
  readonly matter_id: string | null;
  readonly provider_session_ref: string | null;
  readonly metadata: Record<string, unknown> | null;
}

function matterTagLabel(matterId: string): string {
  return matterId.length > 16 ? `${matterId.slice(0, 16)}…` : matterId;
}

function SessionRow(props: {
  readonly run: SessionRowData;
  readonly agentName: string;
  readonly favorited: boolean;
  readonly onToggleFavorite: () => void;
  readonly onArchive: () => void;
  readonly archivePending: boolean;
}): React.ReactElement {
  const { run } = props;
  const archived = isSessionArchived(run.metadata);
  const visual = sessionVisual(run.status);
  const summary = sessionSummary(run.metadata);
  const attention = sessionStatusBucket(run.status) === 'needs-you' && !archived;
  const maHref = maSessionUrl(run.provider_session_ref);
  const rowClass = [
    'runs-row',
    attention ? 'runs-row--attention' : '',
    archived ? 'is-archived' : '',
  ]
    .filter((c) => c.length > 0)
    .join(' ');
  return (
    <div className={rowClass}>
      <span className="runs-row__icon" aria-hidden="true">
        {visual.icon === 'dot' ? (
          <span className="runs-row__dot" style={{ background: visual.iconColor }} />
        ) : (
          <SbIcon name={visual.icon} size={18} style={{ color: visual.iconColor }} />
        )}
      </span>

      <div className="runs-row__body">
        <div className="runs-row__titleline">
          <Link className="runs-row__name" to={`/runs/${encodeURIComponent(run.id)}`}>
            {props.agentName}
          </Link>
          <span
            className={visual.tagClassName}
            style={
              visual.tagDanger
                ? {
                    background: 'var(--color-background-danger-subtle)',
                    color: 'var(--color-text-danger)',
                  }
                : undefined
            }
          >
            {sessionStatusLabel(run.status)}
          </span>
          {archived && <span className="tag tag--grey">Archived</span>}
        </div>
        {summary !== undefined && <div className="runs-row__summary">{summary}</div>}
      </div>

      {/* Deep-link + archive actions. Kept in the DOM (Tab-reachable); CSS
          reveals them on row hover / focus so the resting row stays clean. */}
      <div className="runs-row__actions">
        {langfuseConfigured() && (
          <button
            type="button"
            className="runs-row__action"
            title="Open this session's trace in Langfuse"
            onClick={(e) => {
              e.stopPropagation();
              void openLangfuseTrace(run.id);
            }}
          >
            Langfuse ↗
          </button>
        )}
        {maHref !== undefined && (
          <a
            className="runs-row__action"
            href={maHref}
            target="_blank"
            rel="noopener noreferrer"
            title="Open the managed-agents session (platform.claude.com)"
            onClick={(e) => e.stopPropagation()}
          >
            MA ↗
          </a>
        )}
        {!archived && (
          <button
            type="button"
            className="runs-row__action runs-list-archive"
            title="Soft-archive this session (hides from default list)"
            disabled={props.archivePending}
            onClick={(e) => {
              e.stopPropagation();
              props.onArchive();
            }}
          >
            Archive
          </button>
        )}
      </div>

      {run.matter_id !== null && (
        <span className="tag tag--blue runs-row__matter" title={run.matter_id}>
          {matterTagLabel(run.matter_id)}
        </span>
      )}

      <SessionStar
        favorited={props.favorited}
        sessionLabel={props.agentName}
        onToggle={props.onToggleFavorite}
      />

      <span className="runs-row__time">{sessionTimeOfDay(run.created_at)}</span>
      <SbIcon name="chevron-right" size={14} className="runs-row__chev" />
    </div>
  );
}

// --- Page ------------------------------------------------------------------

export function RunsListPage(): React.ReactElement {
  const [cursors, setCursors] = useState<string[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [bucket, setBucket] = useState<SessionBucket>('all');
  const cursor = cursors[cursors.length - 1];
  const query = trpc.runs.list.useQuery(
    {
      limit: PAGE_SIZE,
      ...(cursor !== undefined ? { cursor } : {}),
      ...(includeArchived ? { includeArchived: true } : {}),
    },
    // Keep the previous page rendered while the paginated/toggled refetch is
    // in flight so the list doesn't flash empty. v5 form (NOT v4
    // `keepPreviousData: true`). fn-52.2 / R2.
    { placeholderData: keepPreviousData },
  );
  const utils = trpc.useUtils();
  const archiveMutation = trpc.runs.archive.useMutation({
    onSuccess: () => {
      void utils.runs.list.invalidate();
    },
  });

  // Resolve agent names client-side (fn-52.1 / R1). One `agents.list` query
  // for the whole page; a `useMemo` map keyed on the data avoids per-row calls.
  const agentsQuery = trpc.agents.list.useQuery({ limit: AGENTS_LIST_LIMIT });
  const agentNames = useMemo(
    () => buildAgentNameMap(agentsQuery.data?.data ?? []),
    [agentsQuery.data],
  );
  const { favorites, isFavorite, toggleFavorite } = useFavorites();

  if (query.isPending) {
    return (
      <section className="runs-list" aria-busy="true">
        <div className="page__header">
          <h1 className="page__title">Sessions</h1>
        </div>
        <p className="caption">Loading sessions…</p>
      </section>
    );
  }
  if (query.error !== null) {
    return (
      <section className="runs-list" role="alert">
        <div className="page__header">
          <h1 className="page__title">Sessions</h1>
        </div>
        <p className="runs-list__error">Failed to load sessions: {query.error.message}</p>
      </section>
    );
  }

  const runs = query.data?.data ?? [];
  const nextCursor = query.data?.next_cursor ?? null;
  const groups = buildSessionGroups({ runs, bucket, favorites, now: new Date() });
  const visibleCount = groups.reduce((n, g) => n + g.runs.length, 0);

  return (
    <section className="runs-list">
      <div className="page__header">
        <h1 className="page__title">Sessions</h1>
        <div className="page__actions" style={{ alignItems: 'center' }}>
          {/* Segmented status filter. A <fieldset>/<legend> gives the group
              its accessible name (per the a11y lint) while `.seg` styles it as
              the design's pill switch. */}
          <fieldset className="seg runs-list__seg">
            <legend className="visually-hidden">Filter sessions by status</legend>
            {SESSION_BUCKETS.map((b) => (
              <button
                key={b.value}
                type="button"
                className="seg__item"
                aria-pressed={bucket === b.value}
                onClick={() => setBucket(b.value)}
              >
                {b.label}
              </button>
            ))}
          </fieldset>
          <QuickStartInline />
        </div>
      </div>

      <div className="runs-list__subbar">
        <label className="runs-list__archived-toggle">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => {
              setCursors([]);
              setIncludeArchived(e.target.checked);
            }}
          />
          <span>Include archived</span>
        </label>
        <span className="caption runs-list__count" aria-live="polite">
          {visibleCount === 1 ? '1 session' : `${visibleCount} sessions`}
          {nextCursor !== null && nextCursor.length > 0 ? '+' : ''}
          {bucket !== 'all' ? ` · ${bucketLabel(bucket)}` : ''}
        </span>
      </div>

      {runs.length === 0 && cursors.length === 0 ? (
        <p className="runs-list__empty">
          No sessions yet. Pick an agent above to start one, or visit the{' '}
          <Link to="/agents">Agents</Link> page for the full flow.
        </p>
      ) : visibleCount === 0 ? (
        // The loaded page has sessions, but none match the active bucket.
        // Client-side filtering only sees the loaded page (buckets are
        // multi-status; the CP `status` param is single-valued).
        <p className="runs-list__empty">
          No sessions on this page match the <strong>{bucketLabel(bucket)}</strong> filter.{' '}
          {bucket !== 'all' && (
            <button
              type="button"
              className="runs-list__link-button"
              onClick={() => setBucket('all')}
            >
              Show all
            </button>
          )}
        </p>
      ) : (
        groups.map((group) => (
          <Fragment key={group.key}>
            <div
              className={`caption-semibold runs-list__group-label${group.pinned ? ' is-pinned' : ''}`}
            >
              {group.pinned && <SbIcon name="star-solid" size={12} />}
              {group.label}
            </div>
            <div className="card runs-list__card">
              {group.runs.map((run) => (
                <SessionRow
                  key={run.id}
                  run={run}
                  agentName={resolveAgentLabel(run.agent_id, agentNames)}
                  favorited={isFavorite(sessionFavoriteKey(run.id))}
                  onToggleFavorite={() => toggleFavorite(sessionFavoriteKey(run.id))}
                  onArchive={() => void archiveMutation.mutateAsync({ sessionId: run.id })}
                  archivePending={archiveMutation.isPending}
                />
              ))}
            </div>
          </Fragment>
        ))
      )}

      <div className="runs-list__pager">
        {cursors.length > 0 && (
          <button
            type="button"
            className="btn btn--tertiary runs-list-prev"
            onClick={() => setCursors((prev) => prev.slice(0, -1))}
          >
            Previous
          </button>
        )}
        {nextCursor !== null && nextCursor.length > 0 && (
          <button
            type="button"
            className="btn btn--tertiary runs-list-next"
            onClick={() => setCursors((prev) => [...prev, nextCursor])}
          >
            Load more
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * fn-2.x — inline "Start a session" widget in the Sessions header. The full
 * Start-with-context flow lives on /agents (matter id, context paste, etc.);
 * this gives a one-click path from the Sessions tab. Picks an agent from the
 * existing agents.list query and posts runs.start with no extras.
 */
function QuickStartInline(): React.ReactElement | null {
  // Same input as the page-level agent-name query so TanStack Query shares one
  // cache entry — one `agents.list` network call serves both. fn-52.1 / R1.
  const agentsQuery = trpc.agents.list.useQuery({ limit: AGENTS_LIST_LIMIT });
  const startMutation = trpc.runs.start.useMutation();
  const navigate = useNavigate();
  const [agentId, setAgentId] = useState<string>('');

  const agents = agentsQuery.data?.data ?? [];
  // Default the dropdown to the first agent once data arrives so the operator
  // can click Start in one shot. Runs in an effect so React isn't queueing
  // setState during render. fn-2.x (CodeRabbit).
  useEffect(() => {
    if (agentId === '' && agents.length > 0) {
      const first = agents[0];
      if (first !== undefined) setAgentId(first.id);
    }
  }, [agentId, agents]);

  if (agentsQuery.isPending) {
    return <span className="caption">Loading agents…</span>;
  }
  if (agents.length === 0) return null;

  const handleStart = async (): Promise<void> => {
    if (agentId === '') return;
    try {
      const res = await startMutation.mutateAsync({ agentId });
      navigate(`/runs/${encodeURIComponent(res.id)}`);
    } catch {
      // Surfaces via startMutation.error below.
    }
  };

  return (
    <div className="runs-list__quickstart">
      <label className="visually-hidden" htmlFor="runs-list-quickstart-agent">
        Agent
      </label>
      <select
        id="runs-list-quickstart-agent"
        className="runs-list__quickstart-select"
        value={agentId}
        onChange={(e) => setAgentId(e.target.value)}
        disabled={startMutation.isPending}
      >
        {agents.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => void handleStart()}
        disabled={agentId === '' || startMutation.isPending}
        className="btn btn--primary runs-list-quickstart-go"
      >
        {startMutation.isPending ? (
          'Starting…'
        ) : (
          <>
            <SbIcon name="sparkles" size={14} />
            Start session
          </>
        )}
      </button>
      {startMutation.error !== null && (
        <span role="alert" className="runs-list__quickstart-error">
          {startMutation.error.message}
        </span>
      )}
    </div>
  );
}
